import * as React from 'react';
import classNames from 'classnames';

// Utils
import { navigate, baseErrorCond } from '@toolkit/util/app';
import { cond } from '@src/shared/src/util/general';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// Constants
import { ROUTES } from '@toolkit/const/app';
import { HOTEL_SORT_VALUES } from '@src/shared/src/const/app';
// Actions
import { filterActions, basketActions } from '@src/shared/src/actions';
// Models
import { OptionModel } from '@src/shared/src/models';
// Interfaces
import { IRootState } from '@src/store';

// Components
import SortBar from '@pod/trips/components/SortBar';
import useBreakpoints from '@toolkit/util/useBreakpoints';
import { CustomSelect } from '@toolkit/ui/CustomSelect';

// Styles
import '../styles/HotelsHeader.scss';

// Props type
type Props = {
  isDisplayHotelList: boolean;
  isSearchingForOnlyHotel: boolean;
  onToggleDisplayList: () => void;
  filteredHotelsLen: number;
  totalHotelsLen: number;
  setHotelDetailsOpenedId: (val: string) => void;
};

const HotelsHeader: React.FC<Props> = ({
  isDisplayHotelList,
  isSearchingForOnlyHotel,
  onToggleDisplayList,
  filteredHotelsLen,
  setHotelDetailsOpenedId,
}) => {
  const { t } = useTranslation();
  const { isMd, isLg } = useBreakpoints();
  const dispatch = useDispatch();
  const currentHotelSortingFilter = useSelector(
    (state: IRootState) => state.filters.hotelSortingFilter.current,
  );
  const basketHotelsLen = useSelector((state: IRootState) => state.checkout.hotels.length);
  const searchId = useSelector((state: IRootState) => state.search.id);

  const sortingOptions = (): OptionModel[] => [
    {
      label: t('HotelsConn.select.option.recommendationSort'),
      value: HOTEL_SORT_VALUES.RECOMMENDATION_SCORE,
    },
    { label: t('HotelsConn.select.option.starsSort'), value: HOTEL_SORT_VALUES.STARS },
    { label: t('HotelsConn.select.option.totalPriceSort'), value: HOTEL_SORT_VALUES.TOTAL_PRICE },
    { label: t('HotelsConn.select.option.ratingsSort'), value: HOTEL_SORT_VALUES.RATING },
    { label: t('HotelsConn.select.option.distanceSort'), value: HOTEL_SORT_VALUES.DISTANCE },
  ];

  const onChangeSortOption = (nOpt: OptionModel) => {
    setHotelDetailsOpenedId('');
    dispatch(
      filterActions.setHotelSortingFilter({
        constraint: { current: nOpt.value },
        onlyUpdateConstraint: false,
      }),
    );
  };

  const onNoHotelNeeded = () => {
    if (basketHotelsLen > 0) {
      dispatch(
        basketActions.removeItemFromBasketAsync.request({
          onSuccess: () => navigate(`${ROUTES.BOOKING.RESERVE}${searchId}`),
          onError: cond(baseErrorCond),
          itemType: 'hotel',
        }),
      );
    } else {
      navigate(`${ROUTES.BOOKING.RESERVE}${searchId}`);
    }
  };

  const notHotelNeededButton = () => {
    return (
      <button className="button button-light" onClick={onNoHotelNeeded}>
        {t('hotelSideBarInfo.checkbox.label.noHotelNeeded')}
      </button>
    );
  };

  const toggleShowListButton = () => (
    <button className="button button-light" onClick={onToggleDisplayList}>
      {isDisplayHotelList ? (
        <span>
          <i className="material-symbols is--first">distance</i>
          {t('HotelsConn.header.label.showMap')}
        </span>
      ) : (
        <span>
          <i className="material-symbols is--first">format_list_bulleted</i>
          {t('HotelsConn.header.label.showList')}
        </span>
      )}
    </button>
  );

  return (
    <div
      className={classNames('hotels-header', {
        'hotels-header-map': !isDisplayHotelList,
      })}>
      {isDisplayHotelList && (
        <>
          <h2 className="hotels-header-title">
            {filteredHotelsLen}&nbsp;
            {t('hotelsConn.header.title')}
            {!isSearchingForOnlyHotel && isMd && notHotelNeededButton()}
          </h2>
          {isMd ? (
            <SortBar
              options={sortingOptions()}
              value={currentHotelSortingFilter}
              onChange={onChangeSortOption}
            />
          ) : (
            <div className="hotels-header-sort">
              {t('HotelsConn.header.label.sort')}:
              <CustomSelect
                options={sortingOptions()}
                selectedValue={currentHotelSortingFilter}
                onChange={onChangeSortOption}
                calculateMinWidth={true}
              />
            </div>
          )}
        </>
      )}
      {!isLg && toggleShowListButton()}
      {!isSearchingForOnlyHotel && (!isMd || !isDisplayHotelList) && notHotelNeededButton()}
    </div>
  );
};

export default HotelsHeader;
